import { Anchor, Text } from '@mantine/core';
import { Link } from 'react-router';
import { getCurrencyIcon } from '~/entities/currency';
import { formatOrder, type PayoutOrder } from '~/entities/payout';
import { getOrderPath, type OrderOrigin, type OrderRouteState } from '~/shared/lib/orderRoutes';
import { type TableRow } from '~/shared/ui/Table';
import { OrderDisplayStatusBadge, OrderPriorityBadge } from '../../../../OrderManagement';
import { Requisite } from '../../../../components';
import { OrderTableColumn } from './columns';

interface PrepareTableRowsProps {
    orders?: PayoutOrder[];
    origin: OrderOrigin;
}

export const prepareTableRows = ({ orders, origin }: PrepareTableRowsProps): TableRow<PayoutOrder>[] => {
    if (!orders) {
        return [];
    }

    return orders.map((order) => {
        const orderId = order.id?.value;
        const routeState = { from: origin } satisfies OrderRouteState;

        return {
            key: orderId ?? 0,
            cells: [
                {
                    key: OrderTableColumn.ID,
                    content: orderId ? (
                        <Anchor
                            component={Link}
                            state={routeState}
                            to={getOrderPath('payout', orderId)}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <Text
                                truncate='end'
                                maw={128}
                            >
                                {orderId}
                            </Text>
                        </Anchor>
                    ) : (
                        <Text
                            truncate='end'
                            maw={128}
                        />
                    ),
                },
                {
                    key: OrderTableColumn.CREATED_AT,
                    content: <Text>{formatOrder.date(order.createdAt)}</Text>,
                },
                {
                    key: OrderTableColumn.EXPIRED_AT,
                    content: <Text>{formatOrder.date(order.expiresAt)}</Text>,
                },
                {
                    key: OrderTableColumn.REQUISITE,
                    content: <Requisite requisiteV2={order.requisiteV2} />,
                },
                {
                    key: OrderTableColumn.AMOUNT,
                    content: <Text>{order.amount?.value}</Text>,
                },
                {
                    key: OrderTableColumn.CURRENCY,
                    content: <Text>{getCurrencyIcon(order.currency)}</Text>,
                },
                {
                    key: OrderTableColumn.RATE,
                    content: <Text>{order.rate?.value ?? '-'}</Text>,
                },
                {
                    key: OrderTableColumn.PRIORITY,
                    content: (
                        <OrderPriorityBadge
                            priority={order.priority}
                            fullWidth
                        />
                    ),
                },
                {
                    key: OrderTableColumn.STATUS,
                    content: (
                        <OrderDisplayStatusBadge
                            order={order}
                            fullWidth
                        />
                    ),
                },
            ],
            item: order,
        };
    });
};
